import { useEffect, useRef, useState } from "react";
import type { FC } from "react";

const VIDEO_SRC = "/Saleem-Enterprises.mp4";
const POSTER_SRC = "/video-poster.webp";

/**
 * This section sits well below the fold, but the video is large. Attaching the
 * source only once the section approaches the viewport keeps it out of the
 * initial page load entirely; autoplay still fires as the visitor arrives.
 */
const AdvanceHMIVideo: FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // No IntersectionObserver (or prerender/older browser): fall back to loading it.
    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      // Start fetching a little early so playback is ready on arrival.
      { rootMargin: "400px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full sm:min-h-[60vh] md:min-h-screen bg-[#ffffff] flex items-center justify-center overflow-hidden"
    >
      {/* Spinner only while the video is actually buffering, over the poster */}
      {shouldLoad && !isReady && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 sm:w-14 sm:h-14 border-4 border-gray-300 border-t-green-500 rounded-full animate-spin"></div>
        </div>
      )}

      <video
        {...(shouldLoad ? { src: VIDEO_SRC } : {})}
        poster={POSTER_SRC}
        preload={shouldLoad ? "auto" : "none"}
        autoPlay
        muted
        loop
        playsInline
        aria-label="Saleem Enterprises intelligent and IoT smart glasshouse facility"
        onLoadedData={() => setIsReady(true)}
        className="w-full h-full object-cover"
      />
    </div>
  );
};

export default AdvanceHMIVideo;
